import { Text } from '#app/components/Primitives/Text.js';
import { Tag } from '#app/components/Tags/Tag.js';
import { useNavigation } from '#app/hooks/useNavigation.js';
import { TextStyles } from '#app/theme/textStyles.js';
import { Avatar } from '../Avatar/Avatar.js';
import { type BaseAvatarProps, type ContraImage } from '../Avatar/types.js';
import { getRoute } from '@contra/routes';
import { type MouseEvent, type Ref } from 'react';
import { css, styled } from 'styled-components';

type AvatarWithDetailsProps = BaseAvatarProps & {
  readonly avatarImage?: ContraImage | null;
  readonly avatarRef?: Ref<HTMLImageElement>;
  readonly className?: string;
  readonly companyUrl?: null | string;
  readonly contributionUrl?: string;
  readonly disableProfileNavigation?: boolean;
  readonly displayUsername?: string;
  readonly headline?: null | string;
  readonly isClickable?: boolean;
  readonly nameRef?: Ref<HTMLDivElement>;
  readonly nameTextStyle?: TextStyles;
  readonly navigateCommunityProfile?: boolean;
  readonly onAvatarClick?: (event: MouseEvent<HTMLElement>) => void;
  readonly onContainerClick?: (event: MouseEvent<HTMLElement>) => void;
  readonly onNameClick?: (event: MouseEvent<HTMLElement>) => void;
  readonly prefix?: string;
  readonly roles?: Array<{ id: string; name: string }>;
  readonly subtitle?: null | string;
  readonly subtitleTextTransform?: 'none' | 'uppercase';
  readonly target?: string;
  readonly userName?: string;
};

const AvatarDetailsWithRolesContainer = styled.div`
  display: inline-block;
  text-align: left;

  & > * + * {
    margin-top: 16px;
  }
`;

const AvatarWithDetailsContainer = styled.div<{ isLinkDisabled: boolean }>`
  ${({ isLinkDisabled }) => css`
    display: flex;
    align-items: center;
    cursor: ${isLinkDisabled ? 'auto' : 'pointer'};

    & > * + * {
      margin-left: 14px;
    }
  `}
`;

const RolesContainer = styled.div`
  > * + * {
    margin-left: 8px;
  }
`;

const NameAndSubtitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  /* max-width: 70%; */
  p {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const SubtitleText = styled(Text)<{ $textTransform: 'none' | 'uppercase' }>`
  ${({ $textTransform, theme }) => css`
    color: ${theme.colorsV2.gray60};
    text-transform: ${$textTransform};
  `}
`;

// TODO: Remove `color` casting once types are updated
export const AvatarWithDetails = ({
  avatarImage,
  avatarRef,
  companyUrl,
  contributionUrl = '',
  deactivated,
  disableProfileNavigation = false,
  displayUsername,
  fallbackColor,
  fallbackName,
  headline,
  isClickable,
  nameRef,
  nameTextStyle = 'subtitleSmall',
  navigateCommunityProfile = false,
  onAvatarClick,
  onContainerClick,
  onNameClick,
  prefix,
  roles = [],
  size = 'lg',
  subtitle,
  subtitleTextTransform = 'none',
  target = undefined,
  userName,
  ...props
}: AvatarWithDetailsProps) => {
  const navigation = useNavigation();
  return (
    <AvatarDetailsWithRolesContainer {...props}>
      <AvatarWithDetailsContainer
        aria-label={userName}
        isLinkDisabled={!displayUsername && !companyUrl && !isClickable}
        onClick={(event) => {
          if (
            displayUsername &&
            navigateCommunityProfile &&
            !disableProfileNavigation
          ) {
            navigation.push(
              getRoute('/community/profile/@username', {
                args: {
                  username: displayUsername,
                },
              }),
            );

            onContainerClick?.(event);

            return;
          }

          if (displayUsername && !disableProfileNavigation) {
            if (!target) {
              navigation.push(
                getRoute('/@username', {
                  args: {
                    username: displayUsername,
                  },
                }),
              );
            }

            if (target === '_blank') {
              window.open(
                getRoute('/@username', {
                  args: {
                    username: displayUsername,
                  },
                }),
                '_blank',
                'noopener,noreferrer',
              );
            }

            onContainerClick?.(event);

            return;
          }

          onContainerClick?.(event);
        }}
        role="option"
      >
        <Avatar
          avatarImage={avatarImage}
          avatarRef={avatarRef}
          borderColor={props.borderColor}
          deactivated={deactivated}
          fallbackColor={fallbackColor}
          fallbackName={fallbackName}
          onClick={onAvatarClick}
          size={size}
        />
        <NameAndSubtitleContainer>
          {prefix ? (
            <Text
              color="gray70"
              textStyle="text-xs-regular"
            >
              {prefix}
            </Text>
          ) : null}
          {headline ? <Text textStyle="overline">{headline}</Text> : null}
          <Text
            onClick={onNameClick}
            ref={nameRef}
            textStyle={nameTextStyle}
          >
            {userName}
          </Text>
          {subtitle ? (
            <SubtitleText
              $textTransform={subtitleTextTransform ?? 'none'}
              textStyle="captionRegular"
            >
              {subtitle}
            </SubtitleText>
          ) : null}
        </NameAndSubtitleContainer>
      </AvatarWithDetailsContainer>

      {roles.length > 0 && (
        <RolesContainer>
          {roles.map((role, index) => (
            <Tag
              key={`role-${role.id}-${index}`}
              label={role.name}
            />
          ))}
        </RolesContainer>
      )}

      {contributionUrl ? (
        <Text
          mt="14px"
          textStyle="captionBold"
        >
          See Contribution
        </Text>
      ) : null}
    </AvatarDetailsWithRolesContainer>
  );
};
